const PRODUCTION_API_BASE_URL = 'https://api.crmai.greybrain.ai';

export const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL?.trim() || PRODUCTION_API_BASE_URL
).replace(/\/+$/, '');

export const FEATURE_ACCESS = {
  // Instagram DMs: Rashmita, Dipti. Facebook DMs: all five telecallers (each sees their own chats).
  SOCIAL_INBOX_EMAILS: ['swainr951@gmail.com', 'sahoodipti969@gamil.com', 'arosmitasahoo9@gamil.com', 'ranibarikjyotshna@gmail.com', 'santaanberhampur@gmail.com'],
  MANUAL_LEAD_EMAILS: ['swainr951@gmail.com'],
  // JEV Assist: released to the counsellors on 1 Oct 2026 (content jev-c1.1). Managers/admins always see it when the API is on.
  JEV_ASSIST_EMAILS: ['reshmarani179@gmail.com', 'ranibarikjyotshna@gmail.com', 'santaanberhampur@gmail.com', 'arosmitasahoo9@gamil.com'] as string[],
} as const;

export function hasSocialAccess(role: string, email: string) {
  return role === 'admin'
    || role === 'manager'
    || role === 'super_admin'
    || FEATURE_ACCESS.SOCIAL_INBOX_EMAILS.includes(email.trim().toLowerCase() as never);
}

export function hasManualLeadAccess(role: string, email: string) {
  return role === 'admin'
    || role === 'manager'
    || role === 'super_admin'
    || FEATURE_ACCESS.MANUAL_LEAD_EMAILS.includes(email.trim().toLowerCase() as never);
}

if (import.meta.env.DEV) {
  console.info('[API] Backend:', API_BASE_URL);
}

export function toWebSocketUrl(path: string, params: Record<string, string> = {}) {
  const url = new URL(path, API_BASE_URL);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
}

export const API_ENDPOINTS = {
  BASE_URL: API_BASE_URL,
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    REGISTER: `${API_BASE_URL}/api/auth/register`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    ME: `${API_BASE_URL}/api/auth/me`,
  },
  LEADS: {
    LIST: `${API_BASE_URL}/api/leads`,
    
    ALL: `${API_BASE_URL}/api/leads/all`,
    DETAIL: (id: string) => `${API_BASE_URL}/api/leads/${id}`,
    UPDATE_STATUS: (id: string) => `${API_BASE_URL}/api/leads/${id}/status`,
    CONFIRM_CONSULTATION: (id: string) => `${API_BASE_URL}/api/leads/${id}/consultation-confirm`,
    ASSIGN: (id: string) => `${API_BASE_URL}/api/leads/${id}/assign`,
    WHATSAPP_MESSAGES: (id: string) => `${API_BASE_URL}/api/leads/${id}/whatsapp/messages`,
    WHATSAPP_READ: (id: string) => `${API_BASE_URL}/api/leads/${id}/whatsapp/read`,
  },
  ACTIONS: {
    CALL: `${API_BASE_URL}/api/action/call`,
    WHATSAPP: `${API_BASE_URL}/api/action/whatsapp`,
  },
  WHATSAPP_INBOX: `${API_BASE_URL}/api/whatsapp/inbox`,
  ADMIN: {
    BLOCKED_NUMBERS: `${API_BASE_URL}/api/admin/blocked-numbers`,
    BLOCKED_NUMBERS_UNBLOCK: `${API_BASE_URL}/api/admin/blocked-numbers/unblock`,
    META_PAGES: `${API_BASE_URL}/api/admin/meta-pages`,
    META_PAGES_TOGGLE: (page_id: string) => `${API_BASE_URL}/api/admin/meta-pages/${encodeURIComponent(page_id)}/toggle`,
    META_PAGES_DELETE: (page_id: string) => `${API_BASE_URL}/api/admin/meta-pages/${encodeURIComponent(page_id)}`,
    IVR_CONFIGS: `${API_BASE_URL}/api/admin/ivr-configs`,
    IVR_CONFIGS_DELETE: (campaign: string) => `${API_BASE_URL}/api/admin/ivr-configs/${encodeURIComponent(campaign)}`,
    PATIENT_FEEDBACK_PREVIEW: `${API_BASE_URL}/api/admin/patient-feedback/upload-preview`,
    PATIENT_FEEDBACK_SEND: `${API_BASE_URL}/api/admin/patient-feedback/send`,
    REVIEWS: `${API_BASE_URL}/api/admin/reviews`,
    REVIEWS_IMPORT_TEXT: `${API_BASE_URL}/api/admin/reviews/import-text`,
    REVIEWS_SYNC_MOS: `${API_BASE_URL}/api/admin/reviews/sync-mos`,
    REVIEWS_MARK_REPLIED: (id: string) => `${API_BASE_URL}/api/admin/reviews/${encodeURIComponent(id)}/mark-replied`,
    REVIEWS_RECOVERY: (id: string) => `${API_BASE_URL}/api/admin/reviews/${encodeURIComponent(id)}/recovery`,
    DASHBOARD_SNAPSHOTS: `${API_BASE_URL}/api/admin/dashboard-snapshots`,
    DASHBOARD_SNAPSHOTS_LATEST: `${API_BASE_URL}/api/admin/dashboard-snapshots/latest`,
    DASHBOARD_SNAPSHOTS_GENERATE: `${API_BASE_URL}/api/admin/dashboard-snapshots/generate`,
    QUALITY_CONTROL_AFFECTED_LEADS: `${API_BASE_URL}/api/admin/quality-control/affected-leads`,
    QUALITY_CONTROL_INITIALIZE: `${API_BASE_URL}/api/admin/quality-control/initialize`,
    ANALYTICS_EOD_LATEST: `${API_BASE_URL}/api/admin/analytics/eod/latest`,
    ANALYTICS_MORNING_LATEST: `${API_BASE_URL}/api/admin/analytics/morning/latest`,
    ANALYTICS_REPORTS: `${API_BASE_URL}/api/admin/analytics/reports`,
    ANALYTICS_EOD_GENERATE: `${API_BASE_URL}/api/admin/analytics/eod/generate`,
    ANALYTICS_MORNING_GENERATE: `${API_BASE_URL}/api/admin/analytics/morning/generate`,
    ANALYTICS_EOD_REGENERATE: `${API_BASE_URL}/api/admin/analytics/eod/regenerate`,
    ANALYTICS_MORNING_REGENERATE: `${API_BASE_URL}/api/admin/analytics/morning/regenerate`,
    ANALYTICS_REPORT_EXPORT: (id: string) => `${API_BASE_URL}/api/admin/analytics/reports/${encodeURIComponent(id)}/export.xlsx`,
  },
  TELECALLERS: `${API_BASE_URL}/api/telecallers`,
  INSIGHTS: (scope: string) => `${API_BASE_URL}/api/insights?scope=${scope}`,
  WHATSAPP_TEAM_INSIGHTS: `${API_BASE_URL}/api/insights/whatsapp-team`,
  MORNING_SUMMARY: (scope: string) => `${API_BASE_URL}/api/insights/morning-summary?scope=${scope}`,
  MORNING_SUMMARY_REGENERATE: `${API_BASE_URL}/api/insights/morning-summary/regenerate`,
  WA_TEMPLATES: `${API_BASE_URL}/api/wa/templates`,
  CALL_QUEUE: {
    ACCEPT: `${API_BASE_URL}/api/call/accept`,
    REJECT: `${API_BASE_URL}/api/call/reject`,
    HANGUP: `${API_BASE_URL}/api/call/hangup`,
    QUEUE: `${API_BASE_URL}/api/call/queue`,
    ACTIVE_CALLS: `${API_BASE_URL}/api/call/active-calls`,
  },
  AGENT: {
    STATUS: `${API_BASE_URL}/api/agent/status`,
  },
  SSE: {
    QUEUE: (agent_id: string) => `${API_BASE_URL}/api/sse/queue?agent_id=${agent_id}`,
    SOCIAL: `${API_BASE_URL}/api/sse/social`,
  },
  WS: {
    WHATSAPP: (leadId: string, token: string) => toWebSocketUrl('/api/ws/whatsapp', {
      lead_id: leadId,
      token,
    }),
    WHATSAPP_LIST: (view: 'queue' | 'unassigned' | 'team', token: string) => toWebSocketUrl('/api/ws/whatsapp-list', {
      view,
      token,
    }),
  },
  SOCIAL: {
    INBOX: `${API_BASE_URL}/api/social/inbox`,
    DETAIL: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}`,
    READ: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/read`,
    STATUS: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/status`,
    ASSIGN: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/assign`,
    REPLY: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/reply`,
    PRIVATE_REPLY: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/private-reply`,
    ASSISTANT: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/assistant`,
    LIKE: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/like`,
    UNLIKE: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/unlike`,
    HIDE: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/hide`,
    UNHIDE: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/unhide`,
    DELETE: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/delete`,
    LEAD: (id: string) => `${API_BASE_URL}/api/social/inbox/${encodeURIComponent(id)}/lead`,
  },
  LIVEKIT: {
    ROOM: `${API_BASE_URL}/api/livekit/room`,
    TOKEN: `${API_BASE_URL}/api/livekit/token`,
    CUSTOMER_TOKEN: `${API_BASE_URL}/api/livekit/customer-token`,
  },
  TEST: {
    QUEUE_EVENT: `${API_BASE_URL}/api/test/queue-event`,
  },
};
